import { useMemo } from 'react'
import type { ViewStyle } from 'react-native'
import { useAutoPosition } from './use-auto-position'
import type { LayoutInfo } from './types'
import {
  PositionAlignment,
  PositionDirection,
} from '~/utils/layout/position-enums'
import { getCombinedPositionStyles } from '~/utils/layout/floating-position-utils'

interface UseFloatingPositionProps {
  side: PositionDirection
  align: PositionAlignment
  autoPosition?: boolean
  rootLayout?: LayoutInfo | null
  contentLayout?: LayoutInfo | null
  gap: number
  screenPadding: number
}

export interface FloatingPositionStyles {
  container: ViewStyle
  wrapper: ViewStyle
}

export interface UseFloatingPositionResult {
  side: PositionDirection
  align: PositionAlignment
  styles: FloatingPositionStyles
}

export function useFloatingPosition({
  side,
  align,
  autoPosition = true,
  rootLayout,
  contentLayout,
  gap,
  screenPadding,
}: UseFloatingPositionProps): UseFloatingPositionResult {
  const sideString = side.toString() as 'top' | 'bottom' | 'left' | 'right'
  const alignString = align.toString() as 'start' | 'middle' | 'end'

  const optimal = useAutoPosition({
    triggerLayout: rootLayout || null,
    contentLayout: contentLayout || null,
    preferredSide: sideString,
    preferredAlign: alignString,
    gap,
    screenPadding,
    autoPosition,
  })

  return useMemo<UseFloatingPositionResult>(() => {
    const finalSide = autoPosition
      ? PositionDirection[
          optimal.side.toUpperCase() as keyof typeof PositionDirection
        ]
      : side

    const finalAlign = autoPosition
      ? PositionAlignment[
          optimal.align.toUpperCase() as keyof typeof PositionAlignment
        ]
      : align

    const styles = getCombinedPositionStyles({
      align: finalAlign,
      side: finalSide,
      contentGap: gap,
      rootLayout,
      contentLayout,
    })

    return {
      side: finalSide,
      align: finalAlign,
      styles,
    }
  }, [autoPosition, side, align, gap, rootLayout, contentLayout, optimal])
}
